import { useEffect, useLayoutEffect } from 'react';
import { buildBreadcrumbList, BREADCRUMB_LD_ID, type Crumb } from './breadcrumbs';
import { buildAlternates, HREFLANG_ATTR } from './hreflang';
import { pathWithLang, DEFAULT_LANG } from './languages';
import { useLang } from './i18n';
import { formatSEOTitle } from './seoTitle';

interface SEOOptions {
  title?: string;
  description?: string;
  /** Social-only description. Defaults to the search description. */
  ogDescription?: string;
  path?: string;
  image?: string;
  noindex?: boolean;
  breadcrumbs?: { name: string; path: string }[];
  disabled?: boolean;
}

const BASE_URL = 'https://thenamewheel.com';
const BRAND = 'TheNameWheel';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og/og-home.png`;
const OG_IMAGE_WIDTH = '1200';
const OG_IMAGE_HEIGHT = '630';
const useClientLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

function setMeta(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setOg(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(url: string) {
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', url);
}

function setRobots(noindex: boolean, nofollow: boolean) {
  setMeta('robots', noindex ? (nofollow ? 'noindex, nofollow' : 'noindex, follow') : 'index, follow');
}

function injectHreflang(path: string, langs: string[]) {
  document.querySelectorAll(`link[${HREFLANG_ATTR}]`).forEach(el => el.remove());
  const frag = document.createDocumentFragment();
  for (const alt of buildAlternates(BASE_URL, path, langs)) {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = alt.hreflang;
    link.href = alt.href;
    link.setAttribute(HREFLANG_ATTR, '');
    frag.appendChild(link);
  }
  document.head.appendChild(frag);
}

function injectBreadcrumbJsonLd(crumbs: Crumb[]) {
  document.getElementById(BREADCRUMB_LD_ID)?.remove();
  const doc = buildBreadcrumbList(crumbs, BASE_URL);
  if (!doc) return;

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = BREADCRUMB_LD_ID;
  script.textContent = JSON.stringify(doc).replace(/<\//g, '<\\/');
  document.head.appendChild(script);
}

/**
 * Keep metadata correct during client-side navigation without charging every
 * initial visit for the full route and translation maps.
 *
 * Production requests are pre-rendered with the final head. If the canonical
 * already matches this route, there is nothing to do. On an SPA navigation it
 * does not match, so the maps are loaded asynchronously and the head is updated.
 */
export function useSEO({
  title,
  description,
  ogDescription,
  path = '',
  image,
  noindex = false,
  breadcrumbs,
  disabled = false,
}: SEOOptions) {
  const { lang } = useLang();
  const rawLangUrl = `${BASE_URL}${pathWithLang(path, lang)}`;
  const langUrl = rawLangUrl === `${BASE_URL}/` || rawLangUrl.endsWith('/')
    ? rawLangUrl
    : `${rawLangUrl}/`;
  const breadcrumbKey = breadcrumbs ? JSON.stringify(breadcrumbs) : '';

  useClientLayoutEffect(() => {
    if (disabled) return;

    const currentCanonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href;
    if (currentCanonical && currentCanonical !== langUrl) {
      // Remove the previous route's hierarchy before paint. The localized
      // replacement is installed after the small SEO map chunk resolves.
      document.getElementById(BREADCRUMB_LD_ID)?.remove();
    }
    if (import.meta.env.PROD && currentCanonical === langUrl) return;

    let active = true;
    void import('./seoRuntime').then((seoData) => {
      if (!active) return;

      const routeSEO = seoData.SEO_BY_ROUTE[path];
      const localised = seoData.localisedSEO(lang, path);
      // Route components usually pass their English defaults. On a localized
      // SPA navigation, the route map must win or hydration rewrites the
      // correctly prerendered metadata back to English.
      const resolvedTitle = localised?.title ?? routeSEO?.title ?? title ?? BRAND;
      const resolvedDescription = localised?.description ?? routeSEO?.description ?? description ?? '';
      const english = {
        title: routeSEO?.title ?? '',
        description: routeSEO?.description ?? '',
      };
      const notTranslated = !seoData.isIndexableIn(lang, path, english);
      const resolvedNoindex = noindex || (routeSEO?.noindex ?? false) || notTranslated;
      const resolvedNofollow = routeSEO?.nofollow ?? false;
      const routeLangs = [DEFAULT_LANG, ...seoData.INDEXABLE_LANGS]
        .filter((value, index, values) => values.indexOf(value) === index)
        .filter(value => seoData.isIndexableIn(value, path, english));
      const resolvedOgDescription = localised?.description
        ?? ogDescription
        ?? routeSEO?.ogDescription
        ?? resolvedDescription;
      const resolvedKeywords = routeSEO?.keywords ?? seoData.DEFAULT_KEYWORDS;
      const resolvedImage = image ?? routeSEO?.ogImage ?? DEFAULT_OG_IMAGE;
      const resolvedCrumbs = breadcrumbs ?? routeSEO?.breadcrumbs;
      const localisedCrumbs = resolvedCrumbs?.map(crumb => ({
        ...crumb,
        path: pathWithLang(crumb.path, lang),
      }));
      const fullTitle = formatSEOTitle(resolvedTitle);

      document.title = fullTitle;
      setMeta('description', resolvedDescription);
      setMeta('keywords', resolvedKeywords);
      setRobots(resolvedNoindex, resolvedNofollow);
      setOg('og:title', fullTitle);
      setOg('og:description', resolvedOgDescription);
      setOg('og:url', langUrl);
      setOg('og:type', path.startsWith('/blog/') ? 'article' : 'website');
      setOg('og:image', resolvedImage);
      setOg('og:image:alt', fullTitle);
      setOg('og:image:width', OG_IMAGE_WIDTH);
      setOg('og:image:height', OG_IMAGE_HEIGHT);
      setOg('og:site_name', BRAND);
      setMeta('twitter:card', 'summary_large_image');
      setMeta('twitter:title', fullTitle);
      setMeta('twitter:description', resolvedOgDescription);
      setMeta('twitter:image', resolvedImage);
      setMeta('twitter:image:alt', fullTitle);
      setCanonical(langUrl);

      if (!resolvedNoindex) injectHreflang(path, routeLangs);
      else document.querySelectorAll(`link[${HREFLANG_ATTR}]`).forEach(el => el.remove());

      if (localisedCrumbs?.length) injectBreadcrumbJsonLd(localisedCrumbs);
      else document.getElementById(BREADCRUMB_LD_ID)?.remove();
    });

    return () => { active = false; };
  }, [title, description, ogDescription, path, image, noindex, breadcrumbKey, disabled, lang, langUrl]);
}
